import { useMemo, useState } from "react";
import { DeviceCategory, isDeviceGateway, VelavuDevice } from "velavu-js-api";
import FloorFilter from "../../../../data/floor-filter";
import { getLocationFloorID } from "../../../../helper/floor-helper";
import { SiteDetailsTab } from "./site-detail";
import { SiteDevicesCacheType } from "./site-devices-cache";

export interface SiteDetailState {
	selectedTab: SiteDetailsTab;
	setSelectedTab: (tab: SiteDetailsTab) => void;

	devices: readonly VelavuDevice[];
	allFloorMapDisplayDevices: VelavuDevice[];
	mapDisplayDevices: VelavuDevice[];

	showAssets: boolean;
	setShowAssets: (showAssets: boolean) => void;
}

const EMPTY_DEVICE_ARRAY: readonly VelavuDevice[] = [];

export function useSiteDetailState(
	siteDevicesCache: SiteDevicesCacheType,
	siteID: string | undefined,
	floorFilter: FloorFilter,
	activeFloorID?: string,
): SiteDetailState {
	const [selectedTab, setSelectedTab] = useState<SiteDetailsTab>(
		SiteDetailsTab.Status,
	);
	const devices =
		siteID === undefined
			? EMPTY_DEVICE_ARRAY
			: (siteDevicesCache.get(siteID) ?? EMPTY_DEVICE_ARRAY);

	const [showAssets, setShowAssets] = useState(false);

	//Derive map display devices
	const allFloorMapDisplayDevices = useMemo(() => {
		if (devices === undefined) {
			return [];
		}

		switch (selectedTab) {
			case SiteDetailsTab.Anchors:
				//Show anchors and gateways
				return devices.filter((device) => {
					return (
						device.category === DeviceCategory.Anchor ||
						isDeviceGateway(device)
					);
				});
			case SiteDetailsTab.Gateways:
				//Show gateways
				return devices.filter((device) => {
					return isDeviceGateway(device);
				});
			default:
				//Show assets
				return devices.filter((device) => {
					return device.asset !== undefined;
				});
		}
	}, [devices, selectedTab]);

	//Filter map display devices by floor
	const mapDisplayDevices = useMemo(() => {
		if (floorFilter === FloorFilter.All || activeFloorID === undefined) {
			return allFloorMapDisplayDevices;
		}

		return allFloorMapDisplayDevices.filter((device) => {
			const floorID = getLocationFloorID(device.location);
			return floorID === undefined || floorID === activeFloorID;
		});
	}, [allFloorMapDisplayDevices, floorFilter, activeFloorID]);

	return useMemo(
		() => ({
			selectedTab,
			setSelectedTab,
			devices,
			allFloorMapDisplayDevices,
			mapDisplayDevices,
			showAssets,
			setShowAssets,
		}),
		[
			selectedTab,
			setSelectedTab,
			devices,
			allFloorMapDisplayDevices,
			mapDisplayDevices,
			showAssets,
			setShowAssets,
		],
	);
}
